import { Component, computed, inject, input, model } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  type AccessPath,
  ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  type AssistantForAnalytics,
  GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  type GroupForAnalytics,
  type TeammateForAnalytics,
} from '@shared';
import { AssistantBuilderFileUploadComponent } from './assistant-builder-file-upload.component';
import { pickSkillMdErrors } from './assistant-builder-skill-md-config.validators';
import {
  AssistantBuilderTraitGenericFilesValue,
  AssistantBuilderTraitGenericFormValues,
  genericConfigurationFileValidator,
} from './assistant-builder-trait-generic-form.validators';
import { getFormErrorMessage } from './assistant-builder.validators';

export type AssistantBuilderTraitGenericForm = {
  [K in keyof AssistantBuilderTraitGenericFormValues]: FormControl<
    AssistantBuilderTraitGenericFormValues[K]
  >;
};

@Component({
  selector: 'squadbox-assistant-builder-trait-generic-form',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    AssistantBuilderFileUploadComponent,
  ],
  template: ` @let form = this.form();
    <form class="flex flex-col gap-4" [formGroup]="form">
      <!-- skill.md configuration file (Instructions) — above Knowledge per Figma (§9) -->
      <div class="flex flex-col gap-2">
        <squadbox-assistant-builder-file-upload
          [label]="
            'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.LABEL'
              | translate
          "
          [placeholder]="
            'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.PLACEHOLDER'
              | translate
          "
          filePickerButtonIcon="plus"
          formControlName="configurationFile"
          [fileSupportedExtensions]="configurationFileSupportedExtensions"
          [allowReservedSkillMdFilename]="true"
          [externalErrorMessage]="configurationFileErrorMessage()"
          [multiple]="false"
          [transactionId]="fileUploadTransactionId()"
          [assistantForAnalytics]="assistantForAnalytics()"
          [assistantAccessPath]="assistantAccessPath()"
          [assistantAccessGroup]="assistantAccessGroup()"
          [assistantTeammate]="assistantTeammate()"
        ></squadbox-assistant-builder-file-upload>
        <ul class="typo-caption text-neutral-700 mt-0 space-y-1 list-disc pl-5">
          <li
            [innerHTML]="
              'ASSISTANTS.SAVE_MODAL.FORM.GENERIC.CONFIGURATION_FILE.HINT'
                | translate: genericConfigurationFileTranslateParams
            "
          ></li>
        </ul>
      </div>

      <div class="py-2 shrink-0" aria-hidden="true">
        <div class="border-t border-neutral-200"></div>
      </div>

      <!-- Sources (Knowledge) -->
      <squadbox-assistant-builder-file-upload
        [label]="'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.LABEL' | translate"
        [placeholder]="
          'ASSISTANTS.SAVE_MODAL.FORM.SOURCES.PLACEHOLDER' | translate
        "
        filePickerButtonIcon="plus"
        formControlName="sourceFiles"
        [fileSupportedExtensions]="documentUploadSupportedExtensions"
        [transactionId]="fileUploadTransactionId()"
        [assistantForAnalytics]="assistantForAnalytics()"
        [assistantAccessPath]="assistantAccessPath()"
        [assistantAccessGroup]="assistantAccessGroup()"
        [assistantTeammate]="assistantTeammate()"
      ></squadbox-assistant-builder-file-upload>
    </form>`,
})
export class AssistantBuilderTraitGenericFormComponent {
  private readonly translate = inject(TranslateService);

  public readonly genericConfigurationFileTranslateParams = {
    configFile: GENERIC_ASSISTANT_CONFIGURATION_FILENAME,
  } as const;

  public readonly fileUploadTransactionId = input.required<string>();
  public readonly assistantForAnalytics = input<
    AssistantForAnalytics | undefined
  >();
  public readonly assistantAccessPath = input<AccessPath | undefined>();
  public readonly assistantAccessGroup = input<GroupForAnalytics | undefined>();
  public readonly assistantTeammate = input<TeammateForAnalytics | undefined>();

  public readonly configurationFileSupportedExtensions = ['md'];

  public readonly documentUploadSupportedExtensions: string[] = [
    ...ASSISTANT_DOCUMENT_UPLOAD_SUPPORTED_EXTENSIONS,
  ];

  public readonly form = model<FormGroup<AssistantBuilderTraitGenericForm>>(
    new FormGroup<AssistantBuilderTraitGenericForm>(
      {
        sourceFiles: new FormControl<AssistantBuilderTraitGenericFilesValue>(
          {
            value: [],
            disabled: false,
          },
          { nonNullable: true },
        ),
        configurationFile:
          new FormControl<AssistantBuilderTraitGenericFilesValue>(
            {
              value: [],
              disabled: false,
            },
            { nonNullable: true },
          ),
      },
      {
        validators: [genericConfigurationFileValidator],
      },
    ),
  );

  /**
   * A language ticker, read by {@link configurationFileErrorMessage} purely so
   * the computed re-runs on a mid-session locale switch.
   *
   * `getFormErrorMessage` resolves through `TranslateService.instant()`, and
   * `instant()` is **not** a signal read — so without this the caption would
   * memoize whichever language was active when the form last emitted an event
   * and stay in it. The builder supports switching locale mid-session
   * (`assistant-builder.page.ts` constructor), and the plan's method binding
   * hid this by re-resolving on every change-detection pass. Same trap and
   * same fix as `response-table-save.component.ts`.
   */
  private readonly uiLanguageTicker = toSignal(this.translate.onLangChange, {
    initialValue: null,
  });

  /**
   * Form-events bridge for {@link configurationFileErrorMessage} — see its doc
   * comment for why a signal is required rather than a method call. Captured
   * once, matching the house pattern for reading `this.form()` in a field.
   */
  private readonly formEvents = toSignal(this.form().events, {
    initialValue: null,
  });

  /**
   * The group-level skill.md error, shown in the configuration zone (#1454).
   *
   * Reads ONLY the two `skillMd*` keys rather than delegating `form.errors`
   * wholesale. `getFormErrorMessage` tests `filesRequired` first and falls back
   * to a bare `INVALID`, so the wholesale form would pin an unrelated error to
   * this zone — and would give every future group validator a vague
   * caption here for free.
   *
   * Visibility keys on the watched controls, NOT on the group:
   * `FormGroup.touched` is true when ANY descendant is touched, so a user
   * typing in an unrelated field would light this zone red unprompted.
   *
   * The delegation passes only the owned keys rather than the whole group: being
   * told a `skillMd*` key is present does NOT establish that `filesRequired` is
   * absent, and `getFormErrorMessage` resolves that one first.
   *
   * A COMPUTED over a form-events bridge, not a plain method call. The plan
   * expected the page's own `statusChanges` bridge to be enough, but the value
   * this reads is mutated by the child upload zone's `effect()` DURING change
   * detection, so a method binding returns one value in the pass and another in
   * dev-mode's check pass — `NG0100`, which failed
   * `assistant-builder.page.spec.ts` C4. Reading a signal makes the value stable
   * within a pass and lands the new one on the next, which the bridge triggers.
   *
   * Bridged on `events`, not `statusChanges`: `markAsTouched()` emits a
   * `TouchedChangeEvent` on the former and nothing on the latter, and visibility
   * here keys on touched/dirty.
   */
  public readonly configurationFileErrorMessage = computed<string | undefined>(
    () => {
      // Dependencies only — the values are read off the form and the
      // translator below.
      this.formEvents();
      this.uiLanguageTicker();
      const form = this.form();
      const watched = ['configurationFile', 'sourceFiles']
        .map((name) => form.get(name))
        .filter((control) => control !== null);
      if (!watched.some((control) => control.dirty || control.touched)) {
        return undefined;
      }
      const owned = pickSkillMdErrors(form.errors);
      if (!owned) {
        return undefined;
      }
      return (
        getFormErrorMessage({ errors: owned }, this.translate) || undefined
      );
    },
  );
}
